import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import type { RawRequestArgs, ResponseOf } from '@tuyau/core/types'
import type { routes } from 'gozoki-backend/registry'
import { api } from '~/lib/tuyau'

type ActiveToursRoute = (typeof routes)['logistics.operations.tours.index']
type ActiveTourRoute = (typeof routes)['logistics.operations.tours.show']
type TourVehiclesRoute = (typeof routes)['logistics.operations.tours.vehicles.index']
type TourLoadChecksRoute = (typeof routes)['logistics.tour_load_checks.index']
/** Paramètres de `GET /logistics/operations/tours`, déduits du validateur du contrôleur. */
export type ActiveToursQuery = RawRequestArgs<ActiveToursRoute>['query']

/**
 * Tournée en vigueur (#890) : un élément de la liste du simulateur (`SimulatorListItem`, pour la
 * carte et les badges), dont `activeScenario` est le scénario de la tournée, plus `tour`, son
 * horaire et son statut prévus.
 */
export type ActiveTourItem = ResponseOf<ActiveToursRoute>['tours'][number]
export type OperationalTourStatus = ActiveTourItem['tour']['status']

/**
 * Détail d'une tournée en vigueur (#891) : l'élément de la liste, plus ses véhicules, ses
 * indicateurs et son déroulé, segment par segment, à l'heure prévue du passage.
 */
export type ActiveTourDetail = ResponseOf<ActiveTourRoute>
export type ActiveTourVehicle = ActiveTourDetail['vehicles'][number]
export type ActiveTourSegment = ActiveTourDetail['segments'][number]
export type ActiveTourPoint = ActiveTourSegment['points'][number]
export type ActiveTourPlannedTime = NonNullable<ActiveTourPoint['arrival']>

/**
 * Le statut prévu bascule au premier départ et à la dernière arrivée : relu chaque minute tant que
 * l'onglet est ouvert (le cadrage de la carte ne bouge pas si l'emprise reste la même).
 */
const ACTIVE_TOURS_STALE_TIME_MS = 60 * 1000

/**
 * Paramètres envoyés : les options des sélecteurs y sont toujours, peu coûteuses, pour que la barre
 * de filtres les lise dans la même réponse que la liste (même clé, une seule lecture).
 */
const withSelectorOptions = (query: ActiveToursQuery): ActiveToursQuery => ({
  ...query,
  withSelectorOptions: true,
})

/**
 * Lecture commune à la liste et aux options : les données précédentes restent affichées pendant le
 * rechargement qui suit un changement de filtre, relu chaque minute.
 */
const ACTIVE_TOURS_READ = {
  placeholderData: keepPreviousData,
  staleTime: ACTIVE_TOURS_STALE_TIME_MS,
  refetchInterval: ACTIVE_TOURS_STALE_TIME_MS,
}

/** Tournées en vigueur filtrées, communes à la carte et au panneau de la gestion opérationnelle. */
export const useActiveToursQuery = (query: ActiveToursQuery) =>
  useQuery(
    api.logistics.operations.tours.index.queryOptions(
      { query: withSelectorOptions(query) },
      ACTIVE_TOURS_READ,
    ),
  )

/**
 * Options des sélecteurs « Cuisines centrales » et « Départements » de l'onglet, celles du
 * simulateur, lues sur la route des tournées pour n'exiger que leur droit de lecture. Même requête
 * que `useActiveToursQuery` pour les mêmes filtres : la barre ne relit pas la liste. Elles ne
 * dépendent pas des filtres : la dernière lecture réussie reste proposée quand la liste échoue,
 * dont le panneau affiche l'erreur : le hook ne la rend pas, pour ne pas la montrer deux fois.
 */
export const useActiveToursSelectorOptions = (query: ActiveToursQuery, enabled: boolean) => {
  const { data, status } = useQuery(
    api.logistics.operations.tours.index.queryOptions(
      { query: withSelectorOptions(query) },
      { ...ACTIVE_TOURS_READ, enabled, select: (response) => response.selectorOptions },
    ),
  )
  const [kept, setKept] = useState(data)
  if (data != null && data !== kept) setKept(data)
  const options = data ?? kept
  return { data: options, status: options == null ? status : ('success' as const) }
}

const activeTourIdsOf = (response: ResponseOf<ActiveToursRoute>) =>
  new Set(response.tours.map((item) => item.tour.id))

/**
 * Tournées en vigueur aujourd'hui, sans les filtres de la liste : le bandeau de la charge (#982)
 * ouvre le détail d'une tournée en vigueur même quand un filtre la masque. Sans filtre, c'est la
 * requête de la liste : rien de plus à lire.
 */
export const useActiveTourIdsQuery = (enabled: boolean) =>
  useQuery(
    api.logistics.operations.tours.index.queryOptions(
      { query: withSelectorOptions({}) },
      // Pas de relecture à la minute : la liste s'en charge sans filtre, et le statut prévu n'y
      // sert pas (en vigueur ou non ne change qu'à minuit).
      {
        placeholderData: keepPreviousData,
        staleTime: ACTIVE_TOURS_STALE_TIME_MS,
        enabled,
        select: activeTourIdsOf,
      },
    ),
  )

/** Détail d'une tournée en vigueur, relu chaque minute comme la liste : son statut prévu bascule. */
export const useActiveTourDetailQuery = (id: number) =>
  useQuery(
    api.logistics.operations.tours.show.queryOptions(
      { params: { id } },
      { staleTime: ACTIVE_TOURS_STALE_TIME_MS, refetchInterval: ACTIVE_TOURS_STALE_TIME_MS },
    ),
  )

/**
 * Charge recalculée chaque nuit d'une tournée validée pour un jour de départ des 3 semaines à venir
 * (#982) : la charge de chaque camion et les avertissements du jour, rouges ou orange.
 */
export type TourLoadCheck = ResponseOf<TourLoadChecksRoute>['checks'][number]
export type TourLoadCheckVehicleLoad = TourLoadCheck['loads'][number]

/** Recalculée une fois par nuit : relue à l'ouverture de l'écran, pas à la minute. */
const TOUR_LOAD_CHECKS_STALE_TIME_MS = 5 * 60 * 1000

/**
 * Charge des tournées validées, toutes à la fois (pas de filtre côté API) : commune à la pastille
 * de l'onglet, au bandeau de la liste et à l'onglet Charge du détail, qui la lisent sur la même clé.
 */
export const useTourLoadChecksQuery = (enabled = true) =>
  useQuery(
    api.logistics.tourLoadChecks.index.queryOptions(
      {},
      { enabled, staleTime: TOUR_LOAD_CHECKS_STALE_TIME_MS },
    ),
  )

/** Véhicule de la liste « Véhicules disponibles » d'un véhicule de la tournée (LU-187). */
export type TourVehicleOption = ResponseOf<TourVehiclesRoute>['vehicles'][number]

/**
 * Véhicules du créneau qui peuvent remplacer `vehicleId` sur la tournée : lus à l'ouverture du menu
 * (`enabled`), et relus à chaque ouverture, la disponibilité bougeant avec les autres tournées.
 */
export const useTourVehicleOptionsQuery = (tourId: number, vehicleId: number, enabled: boolean) =>
  useQuery(
    api.logistics.operations.tours.vehicles.index.queryOptions(
      { params: { id: tourId }, query: { vehicleId } },
      { enabled, staleTime: 0 },
    ),
  )

/**
 * Change un véhicule de la tournée, puis relit son détail (en-tête, bloc Véhicule), la liste des
 * tournées (badges des véhicules) et les menus « Véhicules disponibles ».
 */
export const useChangeTourVehicleMutation = (
  options: Omit<
    NonNullable<
      Parameters<typeof api.logistics.operations.tours.vehicle.update.mutationOptions>[0]
    >,
    'onSettled'
  >,
) => {
  const queryClient = useQueryClient()
  return useMutation(
    api.logistics.operations.tours.vehicle.update.mutationOptions({
      ...options,
      onSettled: () =>
        Promise.all([
          queryClient.invalidateQueries(api.logistics.operations.tours.show.queryFilter()),
          queryClient.invalidateQueries(api.logistics.operations.tours.index.queryFilter()),
          queryClient.invalidateQueries(
            api.logistics.operations.tours.vehicles.index.queryFilter(),
          ),
        ]),
    }),
  )
}

/**
 * Saisit le coût négocié d'une tournée (`PATCH logistics/tours/:id`, LU-104), puis relit son
 * détail (bloc Coût, mention de saisie).
 */
export const useUpdateTourCostMutation = () => {
  const queryClient = useQueryClient()
  return useMutation(
    api.logistics.tours.update.mutationOptions({
      onSettled: () =>
        queryClient.invalidateQueries(api.logistics.operations.tours.show.queryFilter()),
    }),
  )
}
